import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Settings2, Trash2, Power, ExternalLink, KeyRound, ShieldCheck, ShieldOff, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";

const TIPOS = [
  { value: "interno", label: "Interno (Cogniseg)" },
  { value: "externo", label: "Externo (parceiro/marketplace)" },
];

const EMPTY_PLAT = { nome: "", slug: "", descricao: "", ativo: true, tipo: "interno", api_url: "", webhook_url: "", contacto_tecnico: "", logo_url: "", observacoes: "", is_api_connector: false, api_provisioning_prefix: "/api/provisioning" };

export default function Platforms() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [connectors, setConnectors] = useState([]); // status dos conectores API-based
  const [edit, setEdit] = useState(null);
  const [cred, setCred] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get("/platforms"),
        api.get("/connectors/status").catch(() => ({ data: [] })),
      ]);
      setRows(p.data || []);
      setConnectors(c.data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const connectorFor = (slug) => connectors.find((c) => c.connector === slug);

  const salvar = async () => {
    if (!edit.nome.trim() || !edit.slug.trim()) return toast.error("Indique nome e slug.");
    setBusy(true);
    try {
      if (edit.id) {
        await api.put(`/platforms/${edit.id}`, edit);
        toast.success("Plataforma atualizada.");
      } else {
        await api.post("/platforms", edit);
        toast.success("Plataforma criada.");
      }
      setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleAtivo = async (p) => {
    try {
      await api.put(`/platforms/${p.id}`, { ...p, ativo: !p.ativo });
      toast.success(p.ativo ? "Plataforma desativada." : "Plataforma ativada.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remover = async (p) => {
    if (!confirm(`Eliminar plataforma "${p.nome}"?`)) return;
    try {
      await api.delete(`/platforms/${p.id}`);
      toast.success("Eliminada."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openCredentials = (p) => {
    const conn = connectorFor(p.slug);
    if (conn) {
      // Conector API-based: abrir modal dedicado (API URL + API Key + Webhook Secret + testar)
      setCred({ kind: "api", platform: p, connector: conn, api_url: "", api_key: "", webhook_secret: "", settings: null });
    } else {
      // Plataforma generica: modal simples (guarda em /platforms/{id}/settings)
      setCred({ kind: "generic", platform: p, api_key: "", api_secret: "", webhook_secret: "" });
    }
  };

  return (
    <>
      <PageHeader
        overline="Integracoes e Infra"
        title="Plataformas"
        action={<Btn onClick={() => setEdit({ ...EMPTY_PLAT })} testid="new-platform-btn"><Plus size={16} /> Nova plataforma</Btn>}
      />

      {rows.length === 0 && (
        <div className="text-center text-muted2 border border-tint rounded-lg py-12" data-testid="empty-platforms">
          Sem plataformas configuradas.
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="platforms-grid">
        {rows.map((p) => {
          const conn = connectorFor(p.slug);
          return (
            <div key={p.id} data-testid={`platform-${p.slug}`} className={`border rounded-lg p-4 bg-white transition ${p.ativo ? "border-tint hover:shadow-sm" : "border-slate-200 opacity-60"}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy truncate flex items-center gap-2">
                    {p.logo_url && <img src={p.logo_url} alt="" className="w-5 h-5 rounded object-cover" />}
                    {p.nome}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500">{p.slug}</div>
                </div>
                <button onClick={() => toggleAtivo(p)} data-testid={`platform-toggle-${p.slug}`}
                  title={p.ativo ? "Desativar" : "Ativar"}
                  className={`p-1.5 rounded ${p.ativo ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-50"}`}>
                  <Power size={16} />
                </button>
              </div>
              <div className="text-[11px] text-slate-500 line-clamp-2 mb-3 min-h-[2.2em]">{p.descricao || "-"}</div>
              <div className="flex flex-wrap items-center gap-1 mb-3">
                <Badge value={p.tipo} />
                {p.ativo ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">ATIVA</span>
                         : <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">INATIVA</span>}
                {conn && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-0.5">
                    API {conn.configured ? "OK" : "por configurar"}
                  </span>
                )}
                {!conn && p.api_key_configurado && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 inline-flex items-center gap-0.5"><ShieldCheck size={9} /> key</span>}
                {!conn && !p.api_key_configurado && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-0.5"><ShieldOff size={9} /> key</span>}
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <button onClick={() => setEdit({ ...p })} data-testid={`platform-edit-${p.slug}`}
                  className="text-[11px] px-2 py-1 rounded bg-blue text-white hover:bg-[var(--blue-hover)] inline-flex items-center gap-1"><Settings2 size={11} /> Editar</button>
                <button onClick={() => openCredentials(p)} data-testid={`platform-cred-${p.slug}`}
                  className="text-[11px] px-2 py-1 rounded border border-tint text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><KeyRound size={11} /> Credenciais</button>
                {conn && conn.configured && (
                  <button onClick={() => nav(conn.ui_path)} data-testid={`platform-tenants-${p.slug}`}
                    className="text-[11px] px-2 py-1 rounded border border-tint text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"><ExternalLink size={11} /> Tenants</button>
                )}
                <button onClick={() => remover(p)} data-testid={`platform-delete-${p.slug}`}
                  className="text-[11px] px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"><Trash2 size={11} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal editar/criar */}
      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? `Editar ${edit?.nome}` : "Nova plataforma"} testid="platform-modal" size="lg">
        {edit && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nome" value={edit.nome} onChange={(v) => setEdit({ ...edit, nome: v })} testid="platform-nome" />
              <Field label="Slug" value={edit.slug} onChange={(v) => setEdit({ ...edit, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "") })} testid="platform-slug" />
            </div>
            <TextArea label="Descricao" value={edit.descricao} onChange={(v) => setEdit({ ...edit, descricao: v })} testid="platform-descricao" />
            <div className="grid grid-cols-2 gap-3">
              <Selecter label="Tipo" value={edit.tipo} onChange={(v) => setEdit({ ...edit, tipo: v })} options={TIPOS} testid="platform-tipo" />
              <div className="flex items-end pb-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!edit.ativo} onChange={(e) => setEdit({ ...edit, ativo: e.target.checked })} data-testid="platform-ativo" />
                  Ativa
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="API URL" value={edit.api_url} onChange={(v) => setEdit({ ...edit, api_url: v })} testid="platform-apiurl" />
              <Field label="Webhook URL" value={edit.webhook_url} onChange={(v) => setEdit({ ...edit, webhook_url: v })} testid="platform-webhookurl" />
            </div>
            <div className="border border-tint rounded-lg p-3 bg-slate-50/60">
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
                <input type="checkbox" checked={!!edit.is_api_connector} onChange={(e) => setEdit({ ...edit, is_api_connector: e.target.checked })} data-testid="platform-is-api-connector" />
                Conector API (provisionamento via API estilo CogniEtica / Privaxia)
              </label>
              {edit.is_api_connector && (
                <div className="mt-3">
                  <Field label="Prefixo de provisionamento" value={edit.api_provisioning_prefix || "/api/provisioning"} onChange={(v) => setEdit({ ...edit, api_provisioning_prefix: v })} testid="platform-provisioning-prefix" />
                  <p className="text-xs text-muted2 mt-1">Sera exposto em <code>/api/connectors/{edit.slug || "SLUG"}/*</code> (tenants, plans, settings, webhook) apos guardar as credenciais.</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contacto tecnico" value={edit.contacto_tecnico} onChange={(v) => setEdit({ ...edit, contacto_tecnico: v })} testid="platform-contacto" />
              <Field label="Logo URL" value={edit.logo_url} onChange={(v) => setEdit({ ...edit, logo_url: v })} testid="platform-logo" />
            </div>
            <TextArea label="Observacoes" value={edit.observacoes} onChange={(v) => setEdit({ ...edit, observacoes: v })} testid="platform-obs" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEdit(null)}>Cancelar</Btn>
              <Btn onClick={salvar} testid="platform-save" disabled={busy}>{busy ? "..." : "Guardar"}</Btn>
            </div>
          </>
        )}
      </Modal>

      {/* Modal credenciais - API-based ou generico */}
      {cred?.kind === "api" && (
        <ApiConnectorCredentialsModal
          cred={cred}
          onClose={() => setCred(null)}
          onSaved={() => { setCred(null); load(); }}
        />
      )}
      {cred?.kind === "generic" && (
        <Modal open={true} onClose={() => setCred(null)} title={`Credenciais - ${cred.platform?.nome || ""}`} testid="platform-cred-modal">
          <>
            <p className="text-xs text-muted2">Deixe em branco para nao alterar. Os valores sao encriptados em base de dados.</p>
            <Field label="API Key" type="password" value={cred.api_key} onChange={(v) => setCred({ ...cred, api_key: v })} testid="platform-cred-apikey" />
            <Field label="API Secret" type="password" value={cred.api_secret} onChange={(v) => setCred({ ...cred, api_secret: v })} testid="platform-cred-secret" />
            <Field label="Webhook Secret" type="password" value={cred.webhook_secret} onChange={(v) => setCred({ ...cred, webhook_secret: v })} testid="platform-cred-webhook" />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setCred(null)}>Cancelar</Btn>
              <Btn onClick={async () => {
                setBusy(true);
                try {
                  const payload = {};
                  if (cred.api_key) payload.api_key = cred.api_key;
                  if (cred.api_secret) payload.api_secret = cred.api_secret;
                  if (cred.webhook_secret) payload.webhook_secret = cred.webhook_secret;
                  await api.put(`/platforms/${cred.platform.id}/settings`, payload);
                  toast.success("Credenciais atualizadas.");
                  setCred(null); load();
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                finally { setBusy(false); }
              }} testid="platform-cred-save" disabled={busy}>{busy ? "..." : "Guardar credenciais"}</Btn>
            </div>
          </>
        </Modal>
      )}
    </>
  );
}

/**
 * Modal dedicado a conectores API-based (cognietica, privaxia, ...).
 * Reproduz o mesmo formulario que existe na tab "Configuracao" das paginas dedicadas:
 *   URL API + API Key + Webhook Secret (opcional) + Testar ligacao.
 * Grava atraves do endpoint dedicado do conector (que cifra a chave em BD).
 */
function ApiConnectorCredentialsModal({ cred, onClose, onSaved }) {
  const [form, setForm] = useState({ api_url: "", api_key: "", webhook_secret: "" });
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const { platform, connector } = cred;

  useEffect(() => {
    api.get(connector.settings_endpoint).then(({ data }) => {
      setSettings(data);
      setForm((f) => ({ ...f, api_url: data.api_url || "" }));
    }).catch(() => {});
  }, [connector.settings_endpoint]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { api_url: form.api_url };
      if (form.api_key.trim()) body.api_key = form.api_key.trim();
      if (form.webhook_secret.trim()) body.webhook_secret = form.webhook_secret.trim();
      await api.put(connector.settings_endpoint, body);
      toast.success("Credenciais guardadas (cifradas)");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(connector.test_endpoint);
      const service = data.config?.platform_name || data.health?.service || connector.platform_name;
      toast.success(`Ligacao OK - ${service}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title={`Credenciais - ${platform.nome}`} testid={`platform-cred-modal-${platform.slug}`} size="lg">
      <div className="space-y-3">
        <div className="text-xs text-muted2 bg-blue-50 border border-blue-100 rounded p-3">
          Conector <b>{connector.platform_name}</b> - API dedicada. A chave e cifrada em base de dados e nunca vai ao browser.
          Estas credenciais sao partilhadas com a pagina dedicada em <code>{connector.ui_path}</code>.
        </div>
        <Field label="URL da API" value={form.api_url} onChange={(v) => setForm({ ...form, api_url: v })} testid="platform-cred-api-url" placeholder="https://privaxia.cogniseg.net" />
        <Field label="Chave de API (X-API-Key)" type="password" value={form.api_key} onChange={(v) => setForm({ ...form, api_key: v })} testid="platform-cred-api-key"
          placeholder={settings?.api_key_set ? "Definida - deixe vazio para manter" : "Cole a nova chave"} />
        <div className="text-xs text-muted2 -mt-2">
          {settings?.api_key_set
            ? `Atual: ${settings.api_key_masked} (origem: ${settings.api_key_source === "db" ? "definicoes" : "ambiente"})`
            : "Nenhuma chave definida."}
        </div>
        <Field label="Webhook Signing Secret (opcional)" type="password" value={form.webhook_secret} onChange={(v) => setForm({ ...form, webhook_secret: v })} testid="platform-cred-webhook-secret"
          placeholder={settings?.webhook_secret_set ? `Guardado: ${settings.webhook_secret_masked}` : "Se ja tem o secret, cole aqui"} />
        <div className="flex flex-wrap justify-between items-center gap-2 pt-2 border-t border-tint">
          <div className="flex gap-2">
            {settings?.api_key_set && (
              <Btn variant="ghost" onClick={test} disabled={testing} testid="platform-cred-test">
                <RefreshCw size={14} className={testing ? "animate-spin" : ""} /> {testing ? "A testar..." : "Testar ligacao"}
              </Btn>
            )}
          </div>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
            <Btn onClick={save} disabled={busy} testid="platform-cred-save">{busy ? "..." : "Guardar credenciais"}</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}
